// [claude-code 2026-04-05] Sessions dropdown — compact list anchored under the Clock icon, no overlay
// [claude-code 2026-04-19] Loader swapped to HelixVertical (icon-bank) for consistency with chat thinking
import { useEffect, useState, useCallback, useRef } from "react";
import { useBackend } from "../../lib/backend";
import {
  MainContentOverlayPortal,
  type MainContentOverlayAnchorRef,
} from "../layout/MainContentOverlayHost";
import {
  groupConversationsByDate,
  type ConversationSummary,
} from "./session-history-utils";
import { SessionsRail } from "./SessionsRail";
import { publishDeletedConversation } from "./conversation-events";

interface SessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSession: (conversationId: string) => void;
  onNewSession: () => void;
  currentConversationId?: string;
  workspaceId?: string | null;
  historySurface?: string | null;
  anchorRef?: MainContentOverlayAnchorRef;
  portal?: boolean;
}

export function SessionsModal({
  isOpen,
  onClose,
  onSelectSession,
  onNewSession,
  currentConversationId,
  workspaceId,
  historySurface,
  anchorRef,
  portal = false,
}: SessionsModalProps) {
  const backend = useBackend();
  const [sessions, setSessions] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch sessions when dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setSearch("");
    setSelectedIndex(0);
    backend.ai
      .listConversations({ workspaceId, surface: historySurface })
      .then((data) => setSessions(data ?? []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, [isOpen, backend, historySurface, workspaceId]);

  // Focus search on open
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef?.current?.contains(target)) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [anchorRef, isOpen, onClose]);

  // Filter by search
  const filtered = search.trim()
    ? sessions.filter((s) =>
        s.title.toLowerCase().includes(search.toLowerCase()),
      )
    : sessions;

  const grouped = groupConversationsByDate(filtered);
  const flatFiltered = Object.values(grouped).flat();

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, flatFiltered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && flatFiltered[selectedIndex]) {
        onSelectSession(flatFiltered[selectedIndex].id);
        onClose();
      }
    },
    [flatFiltered, selectedIndex, onClose, onSelectSession],
  );

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Scroll selected item into view
  useEffect(() => {
    const item = listRef.current?.querySelector(
      `[data-index="${selectedIndex}"]`,
    );
    item?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const handleDelete = useCallback(
    async (id: string, event: React.MouseEvent) => {
      event.stopPropagation();
      const confirmed = window.confirm(
        "Delete this conversation permanently? This cannot be undone.",
      );
      if (!confirmed) return;
      try {
        await backend.ai.deleteConversation(id);
        setSessions((prev) => prev.filter((s) => s.id !== id));
        publishDeletedConversation(id);
      } catch {
        window.alert(
          "This conversation could not be deleted. Please try again.",
        );
      }
    },
    [backend],
  );

  if (!isOpen) return null;

  const surface = (
    <div
      ref={dropdownRef}
      className={`animate-dropdown-enter ${
        portal ? "w-full" : "absolute right-0 top-full z-50 mt-1 w-[320px]"
      }`}
      onKeyDown={handleKeyDown}
    >
      <SessionsRail
        search={search}
        onSearchChange={setSearch}
        onClose={onClose}
        onNewSession={() => {
          onNewSession();
          onClose();
        }}
        loading={loading}
        grouped={grouped}
        flatSessions={flatFiltered}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        currentConversationId={currentConversationId}
        onSelectSession={(id) => {
          onSelectSession(id);
          onClose();
        }}
        onDeleteSession={handleDelete}
        searchRef={searchRef}
        listRef={listRef}
        variant="popup"
      />
    </div>
  );

  if (portal && anchorRef) {
    return (
      <MainContentOverlayPortal open={isOpen} anchorRef={anchorRef} width={320}>
        {surface}
      </MainContentOverlayPortal>
    );
  }

  return surface;
}
